import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Star, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/latir/Ui";

const API = process.env.REACT_APP_BACKEND_URL;

/* Valoración post-visita — enlace único por reserva (UUID no enumerable).
   Flujo rápido: tocar una estrella guarda la valoración al instante;
   luego se puede enviar un comentario opcional. */
export default function Encuesta() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const [commentSent, setCommentSent] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  useSEO({ title: "Valoración de tu visita | LATIR — Arteconciencia", noindex: true });

  useEffect(() => {
    fetch(`${API}/api/visits/survey/${bookingId}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("404"))))
      .then((d) => {
        setInfo(d);
        const pre = parseInt(params.get("r") || "0", 10);
        const initial = d.rating || (pre >= 1 && pre <= 5 ? pre : 0);
        setComment(d.comment || "");
        if (initial) save(initial, d.comment || "", false);
      })
      .catch(() => setNotFound(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const save = async (r, c, showDone = true) => {
    setRating(r);
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/visits/survey`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ booking_id: bookingId, rating: r, comment: c }),
      });
      if (res.ok && showDone) setDone(true);
      if (res.ok && !showDone) setDone(true);
    } catch {
      /* silencioso: la estrella queda marcada en pantalla */
    } finally {
      setBusy(false);
    }
  };

  const sendComment = async () => {
    await save(rating, comment);
    setCommentSent(true);
  };

  return (
    <div className="paper-grain min-h-screen bg-paper flex items-center justify-center px-6 py-20">
      <div className="w-full max-w-[480px] text-center" data-testid="survey-page">
        {notFound ? (
          <>
            <p className="eyebrow text-latir-red">LATIR</p>
            <p className="mt-6 font-editorial italic text-[1.1rem] text-ink-soft" data-testid="survey-not-found">
              {t("survey.notfound")}
            </p>
            <div className="mt-8">
              <Button to="/" variant="outline" data-testid="survey-home">{t("survey.home")}</Button>
            </div>
          </>
        ) : !info ? (
          <p className="font-ui text-[0.85rem] uppercase tracking-[0.2em] text-ink-mute">…</p>
        ) : !done ? (
          <>
            <p className="eyebrow text-latir-red">LATIR — El placer de vivir</p>
            <h1
              className="mt-5 font-display text-[2rem] sm:text-[2.5rem] leading-[1.05] text-ink"
              style={{ fontFamily: '"Instrument Serif", serif' }}
            >
              {t("survey.title")}
            </h1>
            <p className="mt-4 font-editorial text-[1.02rem] leading-[1.55] text-ink-soft">
              {t("survey.intro")}
            </p>
            <div className="mt-9 flex items-center justify-center gap-2" data-testid="survey-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => save(n, comment)}
                  disabled={busy}
                  aria-label={`${n} de 5`}
                  data-testid={`survey-star-${n}`}
                  className="p-1.5 transition-transform duration-300 hover:scale-110 active:scale-95 disabled:opacity-50"
                >
                  <Star
                    className={`w-9 h-9 sm:w-10 sm:h-10 transition-colors duration-300 ${
                      n <= rating ? "text-latir-red" : "text-ink-mute/40"
                    }`}
                    fill={n <= rating ? "currentColor" : "none"}
                    strokeWidth={1.2}
                  />
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-latir-red/40 text-latir-red">
              <Check className="w-6 h-6" strokeWidth={1.6} />
            </span>
            <h1
              className="mt-6 font-display text-[1.9rem] sm:text-[2.3rem] leading-[1.08] text-ink"
              style={{ fontFamily: '"Instrument Serif", serif' }}
              data-testid="survey-thanks"
            >
              {t("survey.thanks")}
            </h1>
            <div className="mt-2 flex items-center justify-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`w-5 h-5 ${n <= rating ? "text-latir-red" : "text-ink-mute/40"}`}
                  fill={n <= rating ? "currentColor" : "none"}
                  strokeWidth={1.2}
                />
              ))}
            </div>
            <p className="mt-5 font-editorial text-[1rem] leading-[1.55] text-ink-soft">
              {t("survey.thanksText")}
            </p>
            {!commentSent ? (
              <div className="mt-8 text-left">
                <label
                  htmlFor="survey-comment"
                  className="font-ui uppercase tracking-[0.2em] text-[0.66rem] text-ink-mute"
                >
                  {t("survey.comment")}
                </label>
                <textarea
                  id="survey-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  maxLength={600}
                  data-testid="survey-comment"
                  className="mt-2 w-full bg-paper-raised border border-line focus:border-ink outline-none px-4 py-3 font-editorial text-[1rem] text-ink resize-none transition-colors"
                />
                <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={sendComment}
                    disabled={busy}
                    data-testid="survey-send-comment"
                    className="px-6 py-3 bg-ink text-paper-raised hover:bg-latir-red font-ui uppercase tracking-[0.2em] text-[0.72rem] transition-colors disabled:opacity-50"
                  >
                    {t("survey.send")}
                  </button>
                  <Button to="/" variant="ghost" data-testid="survey-home-done">{t("survey.home")}</Button>
                </div>
              </div>
            ) : (
              <p className="mt-8 font-ui uppercase tracking-[0.2em] text-[0.7rem] text-latir-red" data-testid="survey-comment-sent">
                {t("survey.sent")}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
